// TEMP smoke page for UI-upgrade WS-1 + WS-2 validation. Gated behind /_ui-smoke (not in nav).
// Mounts each shadcn primitive (WS-1) AND each shared component + variant (WS-2) once so a
// headless render can assert brand tokens + no console errors.
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Toaster } from '@/components/ui/toaster';
import { useToast as useBaseToast } from '@/components/ui/use-toast';

// WS-2 shared components
import { AlertBanner } from '@/components/shared/AlertBanner';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { Skel, MetricCardSkeleton, TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { useToast as useSharedToast } from '@/components/shared/ToastProvider';

export function UiSmokePage() {
  const { toast } = useBaseToast();
  const { toast: semanticToast } = useSharedToast();
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  return (
      <div style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <h1 data-testid="smoke-title" style={{ fontSize: 22, fontWeight: 700 }}>
          UI Primitive Smoke
        </h1>

        {/* ───────────────────── WS-1 primitives ───────────────────── */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button data-testid="btn-default">Primary</Button>
          <Button variant="destructive" data-testid="btn-destructive">
            Destructive
          </Button>
          <Button variant="outline" data-testid="btn-outline">
            Outline
          </Button>
          <Badge data-testid="badge">Badge</Badge>
        </div>

        <Card data-testid="card">
          <CardHeader>
            <CardTitle>Card Title</CardTitle>
          </CardHeader>
          <CardContent>
            <Skeleton data-testid="skeleton" className="h-4 w-40" />
          </CardContent>
        </Card>

        <Tabs defaultValue="a" data-testid="tabs">
          <TabsList>
            <TabsTrigger value="a">Tab A</TabsTrigger>
            <TabsTrigger value="b">Tab B</TabsTrigger>
          </TabsList>
          <TabsContent value="a">Tab A content</TabsContent>
          <TabsContent value="b">Tab B content</TabsContent>
        </Tabs>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="open-dialog">
                Open Dialog
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Dialog Title</DialogTitle>
              </DialogHeader>
              <p>Dialog body</p>
            </DialogContent>
          </Dialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="open-alert">
                Open Alert
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            data-testid="fire-toast"
            onClick={() => toast({ title: 'Toast fired', description: 'Smoke toast' })}
          >
            Fire Toast
          </Button>
        </div>

        {/* ───────────────────── WS-2 shared components ───────────────────── */}
        <h2 data-testid="ws2-title" style={{ fontSize: 18, fontWeight: 700, marginTop: 12 }}>
          WS-2 Shared Components
        </h2>

        {/* Breadcrumb */}
        <section data-testid="smoke-breadcrumb">
          <Breadcrumb crumbs={[{ label: 'Licenses', to: '/licenses' }, { label: 'LIC-0001' }]} />
        </section>

        {/* AlertBanner x3 */}
        <section data-testid="smoke-alertbanners" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <AlertBanner kind="error" action={() => {}} actionLabel="Retry" onDismiss={() => {}}>
            Error: failed to load fleet telemetry.
          </AlertBanner>
          <AlertBanner kind="warning" action={() => {}} actionLabel="Review">
            Warning: 2 licenses expire within 30 days.
          </AlertBanner>
          <AlertBanner kind="info">
            Info: a new platform version is available.
          </AlertBanner>
        </section>

        {/* EmptyState */}
        <section data-testid="smoke-emptystate">
          <EmptyState
            icon="inbox"
            title="No jobs match this filter"
            body="There are no operations matching the selected status. Try a different filter."
            action={() => {}}
            actionLabel="Dispatch Op"
          />
        </section>

        {/* Skeletons: Skel, MetricCardSkeleton, TableRowSkeleton */}
        <section data-testid="smoke-skeletons" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Skel width={200} height={14} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <TableRowSkeleton cols={[160, 140, 100, 80]} />
              <TableRowSkeleton cols={[160, 140, 100, 80]} />
            </tbody>
          </table>
        </section>

        {/* Toast trigger (semantic, success skin) */}
        <section data-testid="smoke-toast-trigger">
          <Button
            data-testid="fire-shared-toast"
            onClick={() =>
              semanticToast({ kind: 'success', title: 'Operation dispatched', body: 'Job queued to instance.' })
            }
          >
            Fire Shared Toast
          </Button>
        </section>

        {/* ConfirmDialog (danger) — controlled */}
        <section data-testid="smoke-confirm">
          <Button
            variant="destructive"
            data-testid="open-confirm"
            onClick={() => setConfirmOpen(true)}
          >
            Open Confirm Dialog
          </Button>
          <ConfirmDialog
            open={confirmOpen}
            title="Revoke license?"
            body="This permanently revokes the license. This cannot be undone."
            confirmLabel="Revoke License"
            danger
            onConfirm={() => setConfirmOpen(false)}
            onCancel={() => setConfirmOpen(false)}
          />
        </section>

        <Toaster />
      </div>
  );
}
